export interface MemberWeeklyContentItem {
  item_id: string;
  assignees: readonly string[];
  content?: string;
  completed: boolean;
  start_time: number;
  due_time: number;
  priority?: "low" | "medium" | "high" | "urgent";
}

export interface MemberWeeklySubtask<
  T extends MemberWeeklyContentItem = MemberWeeklyContentItem
> {
  id?: string;
  title?: string;
  reviewers: readonly string[];
  content_items: readonly T[];
}

export interface MemberWeeklyTask<
  S extends MemberWeeklySubtask = MemberWeeklySubtask
> {
  id?: string;
  project_id: string;
  status?: string;
  phase?: "initiation" | "development" | "promotion" | "production";
  subtask_summary?: {
    items?: readonly S[];
  };
}

export interface MemberWeeklyContentSelection<
  T extends MemberWeeklyContentItem,
  S extends MemberWeeklySubtask<T>,
  P extends MemberWeeklyTask<S>
> {
  task: P;
  subtask: S;
  contentItem: T;
}

export function isContentItemActiveInRange(
  contentItem: Pick<MemberWeeklyContentItem, "start_time" | "due_time">,
  rangeStart: number,
  rangeEnd: number
) {
  return (
    Number(contentItem.start_time) < rangeEnd &&
    Number(contentItem.due_time) >= rangeStart
  );
}

export function getWeeklyContentActiveDayStarts(
  contentItem: Pick<MemberWeeklyContentItem, "start_time" | "due_time">,
  weekStart: number,
  weekEnd: number
) {
  const dayMs = 24 * 60 * 60 * 1000;
  const dayStarts: number[] = [];
  for (let dayStart = weekStart; dayStart < weekEnd; dayStart += dayMs) {
    if (
      isContentItemActiveInRange(
        contentItem,
        dayStart,
        Math.min(dayStart + dayMs, weekEnd)
      )
    ) {
      dayStarts.push(dayStart);
    }
  }
  return dayStarts;
}

export function selectMemberWeeklyContentItems<
  T extends MemberWeeklyContentItem,
  S extends MemberWeeklySubtask<T>,
  P extends MemberWeeklyTask<S>
>(
  tasks: readonly P[],
  projectIds: ReadonlySet<string>,
  userId: string,
  weekStart: number,
  weekEnd: number
): Array<MemberWeeklyContentSelection<T, S, P>> {
  return selectProjectWeeklyContentItems<T, S, P>(
    tasks,
    projectIds,
    weekStart,
    weekEnd
  ).filter(({ contentItem }) => contentItem.assignees.includes(userId));
}

export function selectProjectWeeklyContentItems<
  T extends MemberWeeklyContentItem,
  S extends MemberWeeklySubtask<T>,
  P extends MemberWeeklyTask<S>
>(
  tasks: readonly P[],
  projectIds: ReadonlySet<string>,
  weekStart: number,
  weekEnd: number
): Array<MemberWeeklyContentSelection<T, S, P>> {
  return tasks
    .filter(
      (task) =>
        projectIds.has(task.project_id) && task.status !== "discarded"
    )
    .flatMap((task) =>
      (task.subtask_summary?.items || [])
        .flatMap((subtask) =>
          subtask.content_items
            .filter((contentItem) =>
              isContentItemActiveInRange(contentItem, weekStart, weekEnd)
            )
            .map((contentItem) => ({
              task,
              subtask,
              contentItem,
            }))
        )
    );
}
